import React, { useEffect, useState } from 'react';
import { Table, Button, Modal, Form, Input, DatePicker, Select, message, Spin, Space, Tabs, Upload } from 'antd';
import dayjs from 'dayjs';
import axios from '../utils/http';
import { getToken } from '../utils/auth';
import { UploadOutlined } from '@ant-design/icons';

const insuranceTypes = [
  { value: '社保', label: '社保' },
  { value: '商保', label: '商保' },
  { value: '工伤', label: '工伤' },
  { value: '其他', label: '其他' },
];

const EmployeeInsurancePage = () => {
  // 基础状态
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form] = Form.useForm();
  const [filterForm] = Form.useForm();
  const [employeeOptions, setEmployeeOptions] = useState([]);
  const [empLoading, setEmpLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('list');

  // 雇主/安责批次
  const [importLoading, setImportLoading] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [checkFilterForm] = Form.useForm();
  const [checkData, setCheckData] = useState([]);
  const [checkLoading, setCheckLoading] = useState(false);
  const [batches, setBatches] = useState([]);
  const [batchLoading, setBatchLoading] = useState(false);
  const [selectedBatch, setSelectedBatch] = useState();

  // 医保批次
  const [medBatches, setMedBatches] = useState([]);
  const [medBatchLoading, setMedBatchLoading] = useState(false);
  const [selectedMedBatch, setSelectedMedBatch] = useState();
  const [medImportLoading, setMedImportLoading] = useState(false);
  const [medImportResult, setMedImportResult] = useState(null);
  const [medCheckData, setMedCheckData] = useState([]);
  const [medCheckLoading, setMedCheckLoading] = useState(false);
  const [medCheckFilterForm] = Form.useForm();

  // 六险核对
  const [sixScope, setSixScope] = useState('employer');
  const [sixBatch, setSixBatch] = useState('');
  const [sixLoading, setSixLoading] = useState(false);
  const [sixResult, setSixResult] = useState(null);
  const [sixError, setSixError] = useState(null);

  // 员工搜索
  const searchEmployees = async (keyword) => {
    if (!keyword) { setEmployeeOptions([]); return; }
    setEmpLoading(true);
    try {
      const res = await axios.get('/employees', { params: { name: keyword }, headers: { 'Authorization': 'Bearer ' + getToken() } });
      setEmployeeOptions((res.data || []).slice(0, 20).map(e => ({ label: `${e.name} (#${e.id})`, value: e.id })));
    } catch (e) { /* ignore */ }
    setEmpLoading(false);
  };

  // 保险列表
  const fetchData = async () => {
    setLoading(true);
    try {
      const params = filterForm.getFieldsValue();
      if (params.dateRange) {
        params.start_date_from = params.dateRange[0]?.format('YYYY-MM-DD');
        params.start_date_to = params.dateRange[1]?.format('YYYY-MM-DD');
        delete params.dateRange;
      }
      const res = await axios.get('/employee-insurance', { params });
      setData(res.data.data || []);
    } catch (err) { message.error(err?.message || '获取保险数据失败'); }
    setLoading(false);
  };

  // 批次列表 (雇主/安责)
  const fetchBatches = async () => {
    setBatchLoading(true);
    try {
      const res = await axios.get('/employer-responsibility-insurance/import-batches');
      setBatches((res.data.data || []).map(b => ({ value: b.import_batch, label: `${b.import_batch} (${b.count})` })));
    } catch (e) { /* ignore */ }
    setBatchLoading(false);
  };

  // 核对数据 (雇主/安责)
  const fetchCheckData = async () => {
    setCheckLoading(true);
    try {
      const params = checkFilterForm.getFieldsValue();
      if (params.importedRange) {
        params.imported_from = params.importedRange[0]?.format('YYYY-MM-DD');
        params.imported_to = params.importedRange[1]?.format('YYYY-MM-DD');
        delete params.importedRange;
      }
      if (selectedBatch) params.import_batch = selectedBatch;
      const res = await axios.get('/employer-responsibility-insurance', { params });
      setCheckData(res.data.data || []);
    } catch (e) { message.error(e?.message || '获取核对数据失败'); }
    setCheckLoading(false);
  };

  // 医保批次列表
  const fetchMedBatches = async () => {
    setMedBatchLoading(true);
    try {
      const res = await axios.get('/medical-insurance-batches/import-batches');
      setMedBatches((res.data.data || []).map(b => ({ value: b.import_batch, label: `${b.import_batch} (${b.count})` })));
    } catch (e) {
      if (e?.response?.status === 404) message.warning('医保批次接口未找到');
    }
    setMedBatchLoading(false);
  };

  // 医保核对数据
  const fetchMedCheckData = async () => {
    setMedCheckLoading(true);
    try {
      const params = medCheckFilterForm.getFieldsValue();
      if (params.importedRange) {
        params.imported_from = params.importedRange[0]?.format('YYYY-MM-DD');
        params.imported_to = params.importedRange[1]?.format('YYYY-MM-DD');
        delete params.importedRange;
      }
      if (selectedMedBatch) params.import_batch = selectedMedBatch;
      const res = await axios.get('/medical-insurance-batches', { params });
      setMedCheckData(res.data.data || []);
    } catch (e) {
      if (e?.response?.status === 404) message.warning('医保数据接口未找到'); else message.error(e?.message || '获取医保数据失败');
    }
    setMedCheckLoading(false);
  };

  // 六险核对
  const fetchSixReconcile = async () => {
    if (!sixBatch) { setSixResult(null); return; }
    setSixLoading(true); setSixError(null);
    try {
      const res = await axios.get('/insurance/reconcile', { params: { batch: sixBatch, scope: sixScope } });
      setSixResult(res.data);
    } catch (e) { setSixError(e?.message || '核对失败'); }
    setSixLoading(false);
  };

  // 初始 & 监听
  useEffect(() => { fetchData(); fetchBatches(); }, []);
  useEffect(() => { if (activeTab === 'check') fetchCheckData(); }, [activeTab, selectedBatch]);
  useEffect(() => { if (activeTab === 'med') { fetchMedBatches(); fetchMedCheckData(); } }, [activeTab, selectedMedBatch]);
  useEffect(() => { if (activeTab === 'six') fetchSixReconcile(); }, [activeTab, sixBatch, sixScope]);

  // 编辑/删除/保存
  const handleEdit = (record) => {
    setEditing(record);
    form.resetFields();
    form.setFieldsValue({
      employee_id: record.employee_id,
      insurance_type: record.insurance_type,
      start_date: record.start_date ? dayjs(record.start_date) : null,
      end_date: record.end_date ? dayjs(record.end_date) : null,
      amount: record.amount,
      note: record.note
    });
    setModalVisible(true);
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`/employee-insurance/${id}`);
      message.success('删除成功');
      fetchData();
    } catch (e) { message.error(e?.message || '删除失败'); }
  };

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      const payload = {
        ...values,
        start_date: values.start_date ? values.start_date.format('YYYY-MM-DD') : null,
        end_date: values.end_date ? values.end_date.format('YYYY-MM-DD') : null,
        amount: (values.amount === undefined || values.amount === '' ? null : Number(values.amount)),
        note: (values.note === undefined || values.note === '' ? null : values.note)
      };
      let saved;
      if (editing) {
        const res = await axios.put(`/employee-insurance/${editing.id}`, payload); saved = res.data?.data; message.success('修改成功');
      } else {
        const res = await axios.post('/employee-insurance', payload); saved = res.data?.data; message.success('新增成功');
      }
      setModalVisible(false); setEditing(null); form.resetFields();
      if (saved) setData(d => [saved, ...d.filter(r => r.id !== saved.id)]); else fetchData();
    } catch (err) {
      if (err && err.errorFields) {
        const msg = err.errorFields.map(f => f.errors.join(',')).join('；'); message.error(msg || '请检查表单输入');
      } else message.error(err?.message || '保存失败');
    }
  };

  const columns = [
    { title: '员工ID', dataIndex: 'employee_id', width: 90 },
    { title: '姓名', dataIndex: 'employee_name', width: 120 },
    { title: '保险类型', dataIndex: 'insurance_type', width: 120 },
    { title: '起始日期', dataIndex: 'start_date', width: 120 },
    { title: '结束日期', dataIndex: 'end_date', width: 120 },
    { title: '金额', dataIndex: 'amount', width: 100 },
    { title: '备注', dataIndex: 'note', ellipsis: true },
    { title: '操作', key: 'action', render: (_, record) => (<><Button type="link" onClick={() => handleEdit(record)}>编辑</Button><Button type="link" danger onClick={() => handleDelete(record.id)}>删除</Button></>) }
  ];

  const tabItems = [
    {
      key: 'list',
      label: '保险列表',
      children: (
        <div style={{ padding:16 }}>
          <Space direction="vertical" style={{ width:'100%' }} size="large">
            <Space style={{ marginBottom:16 }} wrap>
              <Form form={filterForm} layout="inline" onFinish={fetchData}>
                <Form.Item name="name" label="姓名"><Input placeholder="姓名关键字" allowClear style={{ width:140 }} /></Form.Item>
                <Form.Item name="insurance_type" label="类型"><Select allowClear placeholder="类型" options={insuranceTypes} style={{ width:120 }} /></Form.Item>
                <Form.Item name="dateRange" label="起始日期范围"><DatePicker.RangePicker /></Form.Item>
                <Form.Item><Space><Button type="primary" onClick={fetchData}>查询</Button><Button onClick={() => { filterForm.resetFields(); fetchData(); }}>重置</Button></Space></Form.Item>
              </Form>
              <Button onClick={async () => {
                try {
                  const params = filterForm.getFieldsValue();
                  if (params.dateRange) { params.start_date_from = params.dateRange[0]?.format('YYYY-MM-DD'); params.start_date_to = params.dateRange[1]?.format('YYYY-MM-DD'); delete params.dateRange; }
                  const res = await axios.get('/employee-insurance/export', { params, responseType:'blob' });
                  const blob = new Blob([res.data], { type:'text/csv;charset=utf-8;' }); const url = URL.createObjectURL(blob); const a=document.createElement('a'); a.href=url; a.download='employee_insurance.csv'; a.click(); URL.revokeObjectURL(url);
                } catch(e){ message.error(e.message || '导出失败'); }
              }}>导出CSV</Button>
              <Button onClick={async () => {
                try {
                  const params = filterForm.getFieldsValue();
                  if (params.dateRange) { params.start_date_from = params.dateRange[0]?.format('YYYY-MM-DD'); params.start_date_to = params.dateRange[1]?.format('YYYY-MM-DD'); delete params.dateRange; }
                  const res = await axios.get('/employee-insurance/export', { params:{ ...params, format:'excel' }, responseType:'blob' });
                  const blob = new Blob([res.data], { type:'application/vnd.ms-excel' }); const url = URL.createObjectURL(blob); const a=document.createElement('a'); a.href=url; a.download='employee_insurance.xls'; a.click(); URL.revokeObjectURL(url);
                } catch(e){ message.error(e.message || '导出失败'); }
              }}>导出Excel</Button>
              <Button type="primary" onClick={() => { setEditing(null); form.resetFields(); setModalVisible(true); }}>新增保险</Button>
            </Space>
            <Table columns={columns} dataSource={data} rowKey="id" loading={loading} pagination={{ pageSize:20 }} />
            <Modal title={editing ? '编辑保险' : '新增保险'} open={modalVisible} onOk={handleOk} onCancel={() => { setModalVisible(false); setEditing(null); }}>
              <Form form={form} layout="vertical" initialValues={{ employee_id:undefined, insurance_type:undefined, start_date:null, end_date:null, amount:undefined, note:undefined }}>
                <Form.Item name="employee_id" label="员工" rules={[{ required:true, message:'请选择员工' }]}>
                  <Select showSearch allowClear placeholder="输入姓名关键字搜索" filterOption={false} notFoundContent={empLoading ? <Spin size="small" /> : '无匹配'} options={employeeOptions} onSearch={val => searchEmployees(val)} onChange={val => form.setFieldsValue({ employee_id: val })} />
                </Form.Item>
                <Form.Item name="insurance_type" label="保险类型" rules={[{ required:true, message:'请选择保险类型' }]}>
                  <Select options={insuranceTypes} allowClear onChange={val => form.setFieldsValue({ insurance_type: val })} />
                </Form.Item>
                <Form.Item name="start_date" label="起始日期"><DatePicker style={{ width:'100%' }} onChange={val => form.setFieldsValue({ start_date: val })} /></Form.Item>
                <Form.Item name="end_date" label="结束日期"><DatePicker style={{ width:'100%' }} onChange={val => form.setFieldsValue({ end_date: val })} /></Form.Item>
                <Form.Item name="amount" label="金额"><Input type="number" allowClear onChange={e => form.setFieldsValue({ amount: e.target.value })} /></Form.Item>
                <Form.Item name="note" label="备注"><Input allowClear onChange={e => form.setFieldsValue({ note: e.target.value })} /></Form.Item>
              </Form>
            </Modal>
          </Space>
        </div>
      )
    },
    {
      key: 'check',
      label: '保险核对',
      children: (
        <div style={{ padding:16 }}>
          <Space direction="vertical" style={{ width:'100%' }} size="large">
            <div>
              <Upload name="file" accept=".xls,.xlsx,.csv" showUploadList={false} customRequest={async ({ file, onSuccess, onError }) => {
                setImportLoading(true); setImportResult(null);
                const formData = new FormData(); formData.append('file', file);
                try { const res = await axios.post('/employer-responsibility-insurance/import', formData, { headers:{ 'Content-Type':'multipart/form-data' } }); setImportResult(res.data); onSuccess('ok'); message.success('导入成功'); fetchCheckData(); }
                catch(err){ setImportResult(null); onError(err); message.error(err?.message || '导入失败'); }
                setImportLoading(false);
              }}><Button icon={<UploadOutlined />} loading={importLoading}>上传雇主/安责险名单</Button></Upload>
              {importResult && <div style={{ marginTop:12 }}><b>导入结果：</b><pre style={{ background:'#f6f6f6', padding:12, maxHeight:240, overflow:'auto' }}>{JSON.stringify(importResult, null, 2)}</pre></div>}
            </div>
            <Form form={checkFilterForm} layout="inline" onFinish={fetchCheckData}>
              <Form.Item name="name" label="姓名"><Input placeholder="姓名" allowClear /></Form.Item>
              <Form.Item name="department" label="部门"><Input placeholder="部门" allowClear /></Form.Item>
              <Form.Item name="importedRange" label="导入日期"><DatePicker.RangePicker /></Form.Item>
              <Form.Item label="批次"><Select allowClear placeholder="选择批次" loading={batchLoading} style={{ minWidth:160 }} options={batches} value={selectedBatch} onChange={v => setSelectedBatch(v)} /></Form.Item>
              <Form.Item><Space><Button type="primary" onClick={fetchCheckData}>查询</Button><Button onClick={() => { checkFilterForm.resetFields(); setSelectedBatch(undefined); fetchCheckData(); }}>重置</Button><Button onClick={fetchBatches}>刷新批次</Button>{selectedBatch && <Button danger onClick={async () => { try { await axios.delete(`/employer-responsibility-insurance/import-batches/${selectedBatch}`); message.success('批次删除成功'); setSelectedBatch(undefined); fetchBatches(); fetchCheckData(); } catch(e){ message.error(e?.message || '批次删除失败'); } }}>删除当前批次</Button>}</Space></Form.Item>
            </Form>
            {selectedBatch && <div style={{ background:'#fafafa', padding:8, border:'1px solid #eee', borderRadius:4 }}>当前批次：<b>{selectedBatch}</b>；当前筛选记录数：{checkData.length}</div>}
            <Table dataSource={checkData} loading={checkLoading} rowKey="id" pagination={{ pageSize:20 }} columns={[
              { title:'姓名', dataIndex:'name', width:120 },
              { title:'部门', dataIndex:'department', width:120 },
              { title:'雇主险日期', dataIndex:'employer_insurance_date', width:120 },
              { title:'安责险日期', dataIndex:'responsibility_insurance_date', width:120 },
              { title:'雇主险保费', dataIndex:'employer_insurance_fee', width:120 },
              { title:'安责险保费', dataIndex:'responsibility_insurance_fee', width:120 },
              { title:'导入批次', dataIndex:'import_batch', width:120 },
              { title:'导入时间', dataIndex:'imported_at', width:160 }
            ]} />
          </Space>
        </div>
      )
    },
    {
      key: 'med',
      label: '医保批次',
      children: (
        <div style={{ padding:16 }}>
          <Space direction="vertical" style={{ width:'100%' }} size="large">
            <div>
              <Upload name="file" accept=".xls,.xlsx,.csv" showUploadList={false} customRequest={async ({ file, onSuccess, onError }) => {
                setMedImportLoading(true); setMedImportResult(null);
                const formData = new FormData(); formData.append('file', file);
                try { const res = await axios.post('/medical-insurance-batches/import', formData, { headers:{ 'Content-Type':'multipart/form-data' } }); setMedImportResult(res.data); onSuccess('ok'); message.success('导入成功'); fetchMedCheckData(); }
                catch(err){ setMedImportResult(null); onError(err); message.error(err?.message || '导入失败'); }
                setMedImportLoading(false);
              }}><Button icon={<UploadOutlined />} loading={medImportLoading}>上传医保名单</Button></Upload>
              {medImportResult && <div style={{ marginTop:12 }}><b>导入结果：</b><pre style={{ background:'#f6f6f6', padding:12, maxHeight:240, overflow:'auto' }}>{JSON.stringify(medImportResult, null, 2)}</pre></div>}
            </div>
            <Form form={medCheckFilterForm} layout="inline" onFinish={fetchMedCheckData}>
              <Form.Item name="name" label="姓名"><Input placeholder="姓名" allowClear /></Form.Item>
              <Form.Item name="insurance_type" label="险种类型"><Input placeholder="险种类型" allowClear /></Form.Item>
              <Form.Item name="insurance_status" label="参保状态"><Input placeholder="参保状态" allowClear /></Form.Item>
              <Form.Item name="importedRange" label="导入日期"><DatePicker.RangePicker /></Form.Item>
              <Form.Item label="批次"><Select allowClear placeholder="选择批次" loading={medBatchLoading} style={{ minWidth:160 }} options={medBatches} value={selectedMedBatch} onChange={v => setSelectedMedBatch(v)} /></Form.Item>
              <Form.Item><Space><Button type="primary" onClick={fetchMedCheckData}>查询</Button><Button onClick={() => { medCheckFilterForm.resetFields(); setSelectedMedBatch(undefined); fetchMedCheckData(); }}>重置</Button><Button onClick={fetchMedBatches}>刷新批次</Button>{selectedMedBatch && <Button danger onClick={async () => { try { await axios.delete(`/medical-insurance-batches/import-batches/${selectedMedBatch}`); message.success('批次删除成功'); setSelectedMedBatch(undefined); fetchMedBatches(); fetchMedCheckData(); } catch(e){ message.error(e?.message || '批次删除失败'); } }}>删除当前批次</Button>}</Space></Form.Item>
            </Form>
            {selectedMedBatch && <div style={{ background:'#fafafa', padding:8, border:'1px solid #eee', borderRadius:4 }}>当前批次：<b>{selectedMedBatch}</b>；当前筛选记录数：{medCheckData.length}</div>}
            <Table dataSource={medCheckData.filter(r => !['合计','总计','小计'].includes(String(r.name||'').trim()))} loading={medCheckLoading} rowKey="id" pagination={{ pageSize:20 }} columns={[
              { title:'证件类型', dataIndex:'certificate_type', width:110 },
              { title:'证件号码', dataIndex:'certificate_number', width:160 },
              { title:'姓名', dataIndex:'name', width:120 },
              { title:'险种类型', dataIndex:'insurance_type', width:140 },
              { title:'参保状态', dataIndex:'insurance_status', width:120 },
              { title:'本次参保日期', dataIndex:'insurance_date', width:130, render: v => v || '-' },
              { title:'离退休标志', dataIndex:'retire_flag', width:110 }
            ]} />
          </Space>
        </div>
      )
    },
    {
      key: 'six',
      label: '六险核对',
      children: (
        <div style={{ padding:16 }}>
          <Space direction="vertical" style={{ width:'100%' }} size="large">
            <div style={{ display:'flex', flexWrap:'wrap', gap:'12px', alignItems:'flex-end' }}>
              <div style={{ minWidth:220 }}>
                <label style={{ fontSize:12, color:'#475569' }}>选择核对批次</label>
                <Select placeholder="选择雇主/安责批次" style={{ width:'100%', marginTop:4 }} options={batches} loading={batchLoading} value={sixBatch} onChange={v => setSixBatch(v)} allowClear />
              </div>
              <div style={{ minWidth:160 }}>
                <label style={{ fontSize:12, color:'#475569' }}>核对范围</label>
                <Select style={{ width:'100%', marginTop:4 }} value={sixScope} onChange={v => setSixScope(v)} options={[
                  { value:'employer', label:'仅商业(雇主/安责)' },
                  { value:'medical', label:'仅医保批次' },
                  { value:'all', label:'合并两者' }
                ]} />
              </div>
              <Button type="primary" onClick={fetchSixReconcile} disabled={!sixBatch || sixLoading} loading={sixLoading}>执行核对</Button>
              <Button onClick={() => { setSixBatch(''); setSixResult(null); }} disabled={sixLoading}>清空</Button>
            </div>
            {sixError && <div style={{ color:'#dc2626', fontSize:12 }}>{sixError}</div>}
            {!sixError && sixResult && (
              <div style={{ background:'#f8fafc', border:'1px solid #e2e8f0', borderRadius:8, padding:'12px 14px' }}>
                <h4 style={{ margin:'0 0 8px', fontSize:14 }}>核对结果</h4>
                <div style={{ fontSize:12, lineHeight:1.6 }}>
                  批次：<b>{sixResult.batch}</b>；范围：<b>{sixResult.scope}</b><br />
                  在职人数：{sixResult.totalEmployees}；覆盖人数：{sixResult.coveredEmployeesCount}；覆盖率：<b style={{ color: sixResult.coveragePercent>=80?'#16a34a':'#ea580c' }}>{sixResult.coveragePercent}%</b>
                </div>
                <div style={{ marginTop:10 }}>
                  <div style={{ height:10, background:'#e2e8f0', borderRadius:5, overflow:'hidden', position:'relative' }}>
                    <div style={{ position:'absolute', left:0, top:0, bottom:0, width:`${sixResult.coveragePercent}%`, background: sixResult.coveragePercent>=80?'#16a34a':'#f59e0b', transition:'width .4s' }} />
                  </div>
                </div>
                <div style={{ marginTop:12, display:'flex', flexWrap:'wrap', gap:'10px' }}>
                  {Object.entries(sixResult.coverageSummary || {}).map(([k,v]) => (
                    <div key={k} style={{ flex:'1 1 140px', minWidth:140, background:'#fff', border:'1px solid #e2e8f0', borderRadius:8, padding:'8px 10px' }}>
                      <div style={{ fontSize:12, fontWeight:600 }}>{k}</div>
                      <div style={{ fontSize:11, color:'#475569' }}>覆盖人数：{v.count}</div>
                    </div>
                  ))}
                </div>
                <div style={{ marginTop:16 }}>
                  <h5 style={{ margin:'0 0 6px', fontSize:13 }}>缺失员工（{(sixResult.missingEmployees||[]).length}）</h5>
                  {(sixResult.missingEmployees||[]).length === 0 && <div style={{ fontSize:12, color:'#16a34a' }}>全部覆盖，无缺失。</div>}
                  {(sixResult.missingEmployees||[]).length > 0 && (
                    <div style={{ maxHeight:260, overflow:'auto', border:'1px solid #e2e8f0', borderRadius:6 }}>
                      <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
                        <thead><tr style={{ background:'#f1f5f9' }}><th style={{ padding:'6px 8px', textAlign:'left', width:50 }}>ID</th><th style={{ padding:'6px 8px', textAlign:'left', width:120 }}>姓名</th><th style={{ padding:'6px 8px', textAlign:'left', width:160 }}>部门</th><th style={{ padding:'6px 8px', textAlign:'left', width:160 }}>岗位</th></tr></thead>
                        <tbody>
                          {(sixResult.missingEmployees||[]).map(e => (
                            <tr key={e.id} style={{ borderBottom:'1px solid #e2e8f0' }}>
                              <td style={{ padding:'6px 8px' }}>{e.id}</td>
                              <td style={{ padding:'6px 8px' }}>{e.name}</td>
                              <td style={{ padding:'6px 8px' }}>{e.department || '-'}</td>
                              <td style={{ padding:'6px 8px' }}>{e.position || '-'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      <div style={{ marginTop:8 }}>
                        <Button size="small" onClick={() => {
                          const rows = ["ID,姓名,部门,岗位", ...((sixResult.missingEmployees||[]).map(e => `${e.id},${e.name},${e.department||''},${e.position||''}`))];
                          const blob = new Blob([rows.join('\n')], { type:'text/csv;charset=utf-8;' });
                          const url = URL.createObjectURL(blob); const a = document.createElement('a');
                          a.href = url; a.download = `missing_employees_${sixResult.batch}.csv`; a.click(); URL.revokeObjectURL(url);
                        }}>导出缺失名单 CSV</Button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
            {!sixError && !sixResult && sixBatch && !sixLoading && <div style={{ fontSize:12, color:'#64748b' }}>暂无数据，请执行核对。</div>}
            {!sixBatch && <div style={{ fontSize:12, color:'#64748b' }}>请选择批次后执行六险核对。</div>}
          </Space>
        </div>
      )
    }
  ];

  return <Tabs activeKey={activeTab} onChange={setActiveTab} items={tabItems} />;
};

export default EmployeeInsurancePage;
